import { useMemo, useRef } from 'react'
import { BehaviorSubject } from 'rxjs'

import type {
  NavigationListState,
  NavigationSource,
} from '../navigationListContext'

/**
 * This is the main hook that will give you the state of the navigation list,
 * allowing you to directly manipulate it without having to ref on a
 * component. It is required to use and pass on to `NavigationList`.
 */
export function useNavigationListState({
  idList,
  focusItems = false,
  direction = 'vertical',
}: {
  idList: string[]
  focusItems?: boolean
  direction?: 'vertical' | 'horizontal'
}) {
  const selectedId$ = useMemo(
    () =>
      new BehaviorSubject<{ id: string; source: NavigationSource } | null>(
        null
      ),
    []
  )

  /**
   * To prevent useless re-renders, we use a ref here as the list is not
   * needed in the context itself but only in the controls.
   */
  const idListRef = useRef(idList)
  idListRef.current = idList

  const scrollBoundaryRef = useRef<HTMLDivElement>(null)

  return useMemo<NavigationListState>(() => {
    function getSelectedId() {
      return selectedId$.value?.id ?? null
    }

    return {
      focusItems,
      direction,
      selectedId$,
      getSelectedId,
      select(id, source = 'select') {
        selectedId$.next({ id, source })
      },
      selectFirst(source = 'select') {
        selectedId$.next(
          idListRef.current[0] ? { id: idListRef.current[0], source } : null
        )
      },
      deselect() {
        selectedId$.next(null)
      },
      next(source = 'keyboard') {
        const selectedId = getSelectedId()
        const index =
          selectedId !== null ? idListRef.current.indexOf(selectedId) : -1
        const nextId =
          index === -1 || index === idListRef.current.length - 1
            ? idListRef.current[0]
            : idListRef.current[index + 1]
        selectedId$.next(nextId ? { id: nextId, source } : null)
      },
      previous(source = 'keyboard') {
        const selectedId = getSelectedId()
        const index =
          selectedId !== null ? idListRef.current.indexOf(selectedId) : -1
        const previousId =
          index === -1 || index === 0
            ? idListRef.current[idListRef.current.length - 1]
            : idListRef.current[index - 1]
        selectedId$.next(previousId ? { id: previousId, source } : null)
      },
      scrollBoundaryRef,
    }
  }, [focusItems, direction, selectedId$])
}
